import {
	GetProductDocument,
	useDeleteProductImageMutation,
	useUpdateProductImagePrimaryMutation,
} from '@codezo/graphql';
import { Crop, FileText, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { FilePreview, type FilePreviewItem } from '../../components/ui/file-preview';
import { ProductImage, type DisplayCrop } from '../../components/ui/product-image';

/** SVGs are vectors — the raster crop pipeline can't crop them, so crop is disabled. */
const isSvgImage = (mimeType?: string | null, name?: string | null) =>
	mimeType === 'image/svg+xml' || /\.svg$/i.test(name ?? '');

export interface ExistingImage {
	id: string;
	url?: string | null;
	name?: string | null;
	mimeType?: string | null;
	isPrimary?: boolean | null;
	crop?: DisplayCrop | null;
}

interface ProductExistingImagesProps {
	productId: string;
	images: ExistingImage[];
	/** Called after an image is deleted or made primary so the parent can refetch. */
	onChanged: () => void;
	/** Opens the (inline) crop editor for an image. */
	onCropImage: (image: ExistingImage) => void;
	/** Disables actions while a save/upload is in flight. */
	disabled?: boolean;
}

/**
 * Grid of a product's already-uploaded images with crop + delete + set-primary
 * actions. Delete/set-primary operate immediately (they are already persisted);
 * cropping is handed to the parent so the editor can render inline rather than
 * as a modal-on-modal stack.
 */
export function ProductExistingImages({
	productId,
	images,
	onChanged,
	onCropImage,
	disabled,
}: ProductExistingImagesProps) {
	const [deleteImageId, setDeleteImageId] = useState<string | null>(null);
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);

	const previewFiles: FilePreviewItem[] = images
		.filter((img) => img.url)
		.map((img) => ({
			id: img.id,
			url: img.url!,
			name: img.name || 'File',
			mimeType: img.mimeType || 'image/jpeg',
		}));

	const refetchQueries = [{ query: GetProductDocument, variables: { id: productId } }];

	const [deleteImage] = useDeleteProductImageMutation();
	const [setPrimaryImage] = useUpdateProductImagePrimaryMutation();

	const handleSetPrimary = async (imageId: string) => {
		try {
			await setPrimaryImage({ variables: { productId, imageId }, refetchQueries });
			toast.success('Primary image updated');
			onChanged();
		} catch {
			toast.error('Failed to set primary image');
		}
	};

	const handleDeleteImage = async () => {
		if (!deleteImageId) return;
		try {
			await deleteImage({ variables: { productId, imageId: deleteImageId }, refetchQueries });
			toast.success('Image deleted');
			onChanged();
		} catch {
			toast.error('Failed to delete image');
		}
		setDeleteImageId(null);
	};

	if (images.length === 0) return null;

	return (
		<div className="border-t border-line-1 pt-4">
			<div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-text-3">
				Uploaded ({images.length})
			</div>
			<div className="grid grid-cols-3 gap-2">
				{images.map((img) => (
					<div
						key={img.id}
						onClick={() => {
							const idx = previewFiles.findIndex((f) => f.id === img.id);
							if (idx >= 0) setPreviewIndex(idx);
						}}
						className="group relative cursor-pointer overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-fill transition-colors hover:border-line-2"
					>
						{img.mimeType?.startsWith('image/') ? (
							<ProductImage image={img} className="aspect-square w-full" />
						) : (
							<div className="flex aspect-square items-center justify-center">
								<FileText size={24} className="text-text-4" />
							</div>
						)}
						{img.isPrimary && (
							<div className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded-[var(--r-1)] bg-black/70 px-1.5 py-0.5 text-2xs font-medium text-white backdrop-blur-sm">
								<Star size={10} /> Primary
							</div>
						)}
						{!disabled && (
							<div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
								{img.mimeType?.startsWith('image/') && (
									<button
										type="button"
										disabled={isSvgImage(img.mimeType, img.name)}
										onClick={(e) => {
											e.stopPropagation();
											onCropImage(img);
										}}
										title={
											isSvgImage(img.mimeType, img.name)
												? "SVG images can't be cropped"
												: 'Crop'
										}
										className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-black/60 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white disabled:cursor-default disabled:opacity-40 disabled:hover:bg-black/60 disabled:hover:text-white/80"
									>
										<Crop size={11} />
									</button>
								)}
								{!img.isPrimary && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											handleSetPrimary(img.id);
										}}
										title="Set as primary"
										className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-black/60 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white"
									>
										<Star size={11} />
									</button>
								)}
								<button
									onClick={(e) => {
										e.stopPropagation();
										setDeleteImageId(img.id);
									}}
									className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-black/60 text-white/80 backdrop-blur-sm transition-colors hover:bg-red-600 hover:text-white"
								>
									<Trash2 size={11} />
								</button>
							</div>
						)}
					</div>
				))}
			</div>

			<ConfirmDialog
				open={!!deleteImageId}
				onOpenChange={(v) => !v && setDeleteImageId(null)}
				title="Delete image"
				description="Are you sure? This cannot be undone."
				confirmLabel="Delete"
				onConfirm={handleDeleteImage}
			/>

			{previewIndex !== null && (
				<FilePreview
					files={previewFiles}
					initialIndex={previewIndex}
					onClose={() => setPreviewIndex(null)}
				/>
			)}
		</div>
	);
}
